import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import StatusPill from './StatusPill';
import ActionBar from './ActionBar';
import './PageHeader.css';

// The header's one status, drawn by the shared pill. Every caller passes an
// entry already worked out from the pill maps ({ label, bg }).
function HeaderStatus({ status }) {
    return <StatusPill pill={status} className="page-header-status" />;
}

HeaderStatus.propTypes = {
    status: PropTypes.shape({
        label: PropTypes.string.isRequired,
        bg: PropTypes.string.isRequired,
    }).isRequired,
};

/**
 * The top of a detail page: a back link, the thing's own name, one status,
 * one line of key facts, and the page's actions on the right. `children`
 * sits under the name, for what the page most needs read first. On a phone
 * the actions stack after `children` and the primary is pinned to the bottom
 * of the screen (ActionBar). `secondaryBesideTitle` keeps the secondary
 * actions next to the name on a phone, at their own width, so they do not
 * interrupt what `children` says.
 */
function PageHeader({
    backTo,
    backLabel,
    title,
    status = null,
    facts = [],
    primaryAction = null,
    secondaryActions = null,
    secondaryBesideTitle = false,
    children = null,
}) {
    const shownFacts = facts.filter(Boolean);
    return (
        <header
            className={`page-header mb-4${secondaryBesideTitle ? ' page-header-beside' : ''}`}
        >
            {backTo && (
                <Link to={backTo} className="d-inline-block mb-2">
                    <span aria-hidden="true">← </span>
                    {backLabel}
                </Link>
            )}
            <div className="page-header-body">
                <div className="page-header-text">
                    <h1 className="h2 mb-2 text-break">{title}</h1>
                    {(status || shownFacts.length > 0) && (
                        <div className="d-flex flex-wrap align-items-center column-gap-3 row-gap-1">
                            {status && <HeaderStatus status={status} />}
                            {shownFacts.length > 0 && (
                                <div className="page-header-facts-wrap">
                                    <ul
                                        className="page-header-facts list-unstyled mb-0 text-body-secondary"
                                        aria-label="Key facts"
                                    >
                                        {shownFacts.map((fact, index) => (
                                            <li key={index}>{fact}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <ActionBar
                    className="page-header-actions"
                    primary={primaryAction}
                    secondary={secondaryActions}
                    stickyPrimaryOnPhone
                    label="Actions"
                />
                {children && (
                    <div className="page-header-children">{children}</div>
                )}
            </div>
        </header>
    );
}

PageHeader.propTypes = {
    backTo: PropTypes.string,
    backLabel: PropTypes.string,
    title: PropTypes.node.isRequired,
    status: PropTypes.shape({
        label: PropTypes.string.isRequired,
        bg: PropTypes.string.isRequired,
    }),
    facts: PropTypes.arrayOf(PropTypes.node),
    primaryAction: PropTypes.node,
    secondaryActions: PropTypes.node,
    secondaryBesideTitle: PropTypes.bool,
    children: PropTypes.node,
};

export default PageHeader;
